import React from 'react'
import { Button, AutoComplete, Input, Form, Spin } from 'antd'
import { withTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import withRouterParams from '../common/RouterUtils'
import ReactGA from 'react-ga4'
import { getUserId } from '../../Utils/AnalyticsUtils'
import {
  autocompleteSearchRequest,
  clearAutocompleteOptions,
  searchCatalogProducts,
  getProductsByLineaCatalogo,
  setFilterSelected,
} from '../../actions'
import { getProductImagePath } from '../../lib/CatalogUtils'
import { CATALOG_ROUTES } from '../../constants/CatalogRoutes'

import { SearchOutlined } from '@ant-design/icons'
const FormItem = Form.Item

export const defaultSearchComboLayout = {
  xs: { span: 24 },
  sm: { span: 24 },
  md: { span: 24 },
  lg: { span: 6 },
  xl: { span: 6 },
}

export const defaultSearchInputLayout = {
  xs: { span: 24 },
  sm: { span: 24 },
  md: { span: 24 },
  lg: { span: 15 },
  xl: { span: 15 },
}

const PARAM_FILTER = 'filtro'
const PARAM_SORT = 'orden'
const PARAM_LINEACATALOGOID = 'lineaCatalogoId'

class SearchBox extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      catalogLineaSearch: null,
      searchFilter: null,
      searchText: null,
      style: Object.assign({}, props.style, 'width: 120'),
      options: [],
      initialValues: {
        searchSort: null,
        searchText: '',
        catalogFilters: {},
      },
    }
  }

  componentDidMount() {
    const params = this.getParams()
    const initialValues = {
      searchFilter: params[PARAM_SORT],
      searchText: params[PARAM_FILTER] ? params[PARAM_FILTER] : '',
    }
    this.setState(initialValues)
  }

  static getDerivedStateFromProps(nextProps, state) {
    const { searchOptions, initialValues } = nextProps

    if (nextProps.autocompleteOptions !== state.options) {
      return {
        options: nextProps.autocompleteOptions,
      }
    }

    if (!state.initialValues.catalogFilters && nextProps.initialValues) {
      const el = searchOptions
        ? searchOptions.find((el) => el.value === initialValues.searchSort)
        : null
      const selectedSort = el
        ? el.value
        : searchOptions && searchOptions.length > 0
          ? searchOptions[0].value
          : null
      return {
        searchText: nextProps.searchText || state.searchText,
        searchFilter: selectedSort,
        initialValues,
      }
    } else if (
      initialValues.searchSort !== state.initialValues.searchSort ||
      initialValues.searchText !== state.initialValues.searchText
    ) {
      const el = searchOptions
        ? searchOptions.find((el) => el.value === initialValues.searchSort)
        : null
      const selectedSort = el
        ? el.value
        : searchOptions && searchOptions.length > 0
          ? searchOptions[0].value
          : null
      return {
        searchFilter: selectedSort,
        searchText: nextProps.searchText || state.searchText,
        initialValues,
      }
    } else {
      return null
    }
  }

  handleSubmit = () => {
    const { searchFilter, searchText } = this.state
    if (searchText) {
      this.handleSearchProducts({
        searchFilter,
        searchText,
      })
    }
  }

  handlePotentialSubmit = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.handleSubmit()
    }
  }

  handleChange = (value) => {
    this.setState({ searchText: value })
    this.updateURL({ [PARAM_FILTER]: value }, false)

    if (value.length >= 3) {
      this.props.autocompleteSearchRequest(value)
    } else {
      this.setState({ options: [] })
      this.props.clearAutocompleteOptions()
    }
  }

  handleSearchChange = (value) => {
    this.props.onChangeFilter(value)
    this.setState({
      searchFilter: value,
    })
  }

  handleSearchProducts = (query) => {
    const { user, router } = this.props
    const queryParams = {
      [PARAM_FILTER]: query.searchText,
      [PARAM_SORT]: query.searchFilter,
    }

    if (router && router.pathname !== CATALOG_ROUTES.CATALOG) {
      router.push({ pathname: CATALOG_ROUTES.CATALOG, query: queryParams })
    } else {
      this.updateURL(queryParams, true)
    }

    // Explicit and computed fresh here rather than relying on gtag's "set" defaults
    // (initializeGoogleAnalytics / sagas/Auth.js) — confirmed via GA4 DebugView that those
    // don't reliably apply to later batched hits, so each event has to carry its own current
    // identity instead of assuming it'll inherit one.
    ReactGA.event('search', {
      search_term: query.searchText,
      iturriaUser: getUserId(user),
    })

    this.props.searchCatalogProducts({
      order: query.searchFilter,
      filter: query.searchText,
      page: 1,
      pageSize: 12,
      ...query.catalogFilters,
    })
  }

  handleSelect = (selection) => {
    this.setState(
      {
        searchText: selection,
      },
      () => {
        this.handleSubmit()
      },
    )
  }

  render() {
    const { t, isAutocompletingSearch } = this.props
    const { options, searchText } = this.state

    return (
      <Form onFinish={this.handleSubmit}>
        <FormItem>
          <AutoComplete
            value={searchText}
            style={this.props.style}
            options={options.map((item) => ({
              value: item.codigodearticulo,
              label: (
                <div>
                  <img
                    src={getProductImagePath(item)}
                    alt={item.descripcioncatalogo}
                    style={{ width: '30px', marginRight: '10px' }}
                  />
                  {item.codigodearticulo} - {item.descripcioncatalogo}
                </div>
              ),
            }))}
            onSearch={this.handleChange}
            onSelect={this.handleSelect}
            autoFocus={this.props.autoFocus}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: 450 }}
          >
            <Input.Search
              onKeyDown={this.handlePotentialSubmit}
              allowClear={!isAutocompletingSearch}
              suffix={isAutocompletingSearch ? <Spin size='small' /> : null}
              placeholder={t('Search')}
              size='large'
              enterButton={
                <Button
                  type='primary'
                  icon={<SearchOutlined style={{ color: 'white' }} onClick={this.handleSubmit} />}
                />
              }
            />
          </AutoComplete>
        </FormItem>
      </Form>
    )
  }
}

SearchBox.propTypes = {
  defaultSearchComboLayout: PropTypes.object,
  defaultSearchInputLayout: PropTypes.object,
  autoFocus: PropTypes.bool,
}

SearchBox.defaultProps = {
  defaultSearchComboLayout,
  defaultSearchInputLayout,
  initialValues: {},
}

const mapStateToProps = ({ catalog, products, auth }) => {
  const {
    autocompleteOptions,
    products: catalogProducts,
    isSearching,
    isAutocompletingSearch,
    selectedCatalogLine,
  } = catalog
  return {
    autocompleteOptions,
    products: catalogProducts,
    isSearching,
    isAutocompletingSearch,
    selectedCatalogLine,
    user: auth.user,
  }
}

const mapDispatchToProps = {
  autocompleteSearchRequest,
  clearAutocompleteOptions,
  searchCatalogProducts,
  getProductsByLineaCatalogo,
  setFilterSelected,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  withTranslation()(
    withRouterParams(SearchBox, {
      [PARAM_FILTER]: PARAM_FILTER,
      [PARAM_SORT]: PARAM_SORT,
      [PARAM_LINEACATALOGOID]: PARAM_LINEACATALOGOID,
    }),
  ),
)
